import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Button,
  Alert,
  Row,
} from "reactstrap";
import BreadCrumb from "../../components/Common/BreadCrumb";
import useAuthUser from "../../components/Hooks/useAuthUser";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";
import {
  updateProfile,
  resetUpdateProfileStatus,
  changePassword,
  resetChangePasswordStatus,
  resetChangePasswordError,
} from "../../store/actions";
import userDummyImg from "../../assets/images/users/user-dummy-img.jpg";
import axios from "axios";
import getAuthUserStorage from "../../helpers/getAuthUserStorage";
import formatPersonName from "../../helpers/formatPersonName";
import "./styles.css";

interface ProfileFormValues {
  firstName: string;
  lastName: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Account: React.FC = () => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", { page: t("account.pageTitle") });
  const history = useHistory();
  const dispatch = useDispatch();
  const { userProfile, roles } = useAuthUser();
  const { isUserSwitch } = useSelector((state: any) => ({
    isUserSwitch: state.Profile.userSwitch.isUserSwitch,
  }));

  const { updateStatus, updateError, updateSuccess } = useSelector(
    (state: any) => ({
      updateStatus: state.Profile.updateStatus,
      updateError: state.Profile.updateError,
      updateSuccess: state.Profile.updateSuccess,
    })
  );

  const { status: passwordStatus, error: passwordError } = useSelector(
    (state: any) => ({
      status: state.ChangePassword.status,
      error: state.ChangePassword.error,
    })
  );

  // Profile picture state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  // Password validation states
  const [invalidLength, setInvalidLength] = useState<boolean>(true);
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
  const [editingName, setEditingName] = useState<boolean>(false);
  const [missingNumber, setMissingNumber] = useState<boolean>(true);
  const [missingLetter, setMissingLetter] = useState<boolean>(true);

  // Map role codes to display labels
  const getRoleLabel = (rolesArray: string[] | undefined): string => {
    if (!rolesArray || rolesArray.length === 0)
      return t("account.roleFallback");
    if (rolesArray.includes("SUPER_ADMIN"))
      return t("common.roles.SUPER_ADMIN");
    if (rolesArray.includes("ADMIN")) return t("common.roles.ADMIN");
    if (rolesArray.includes("TENANT_ADMIN"))
      return t("common.roles.TENANT_ADMIN");
    if (rolesArray.includes("TENANT_USER"))
      return t("common.roles.TENANT_USER");
    return t("account.roleFallback");
  };

  // Redirect if user switch is active
  useEffect(() => {
    if (isUserSwitch) history.push("/dashboard");
  }, [isUserSwitch, history]);

  // Reset update status on component mount
  useEffect(() => {
    dispatch(resetUpdateProfileStatus());
    dispatch(resetChangePasswordStatus());
    dispatch(resetChangePasswordError());
  }, [dispatch]);

  // Handle profile picture file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so choosing the same file again still fires onChange
    event.target.value = "";
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert(t("account.picture.invalidType"));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t("account.picture.tooLarge"));
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle profile picture upload
  const handleImageUpload = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    setUploadingImage(true);
    try {
      // Get presigned URL from backend
      const authUserStorage = getAuthUserStorage();
      console.log("Auth user storage:", authUserStorage);
      const token = authUserStorage?.token;
      console.log("Token:", token);

      if (!token) {
        alert(t("account.picture.tokenMissing"));
        setUploadingImage(false);
        return null;
      }

      const uploadUrlResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/generate-upload-url`,
        {
          fileType: selectedFile.type,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { uploadUrl, fileUrl } =
        uploadUrlResponse.data || uploadUrlResponse;

      // Upload file to S3 using fetch (not axios) to avoid sending Authorization header
      // Presigned URLs already contain authentication in query parameters
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `S3 upload failed: ${uploadResponse.status} ${
            uploadResponse.statusText
          } ${await uploadResponse.text()}`
        );
      }

      setUploadingImage(false);
      return fileUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      alert(t("account.picture.uploadFailed"));
      setUploadingImage(false);
      return null;
    }
  };

  // Update profile form submission to include image upload
  const handleProfileSubmit = async (
    values: ProfileFormValues,
    { setSubmitting }: any
  ) => {
    let profilePictureUrl = userProfile?.profilePictureUrl;

    try {
      // Upload image if a new one was selected
      if (selectedFile) {
        const uploadedUrl = await handleImageUpload();
        if (uploadedUrl) {
          // Only update the URL if upload was successful
          profilePictureUrl = uploadedUrl;
          // Clear selection after successful upload
          setSelectedFile(null);
          setPreviewUrl(null);
        }
      }

      // Build the update payload, only including profilePictureUrl if it has a value
      const updatePayload: any = {
        firstName: values.firstName,
        lastName: values.lastName,
      };

      // Only include profilePictureUrl if it's not undefined
      if (profilePictureUrl !== undefined) {
        updatePayload.profilePictureUrl = profilePictureUrl;
      }

      // Always dispatch the profile update, even if image upload failed
      dispatch(updateProfile(updatePayload));
    } catch (error) {
      console.error("Error in handleProfileSubmit:", error);
    } finally {
      // Reset Formik's submitting state
      setSubmitting(false);
    }
  };

  // Profile form validation
  const profileValidation = useFormik<ProfileFormValues>({
    enableReinitialize: true,
    initialValues: {
      firstName: userProfile?.first_name || userProfile?.firstName || "",
      lastName: userProfile?.last_name || userProfile?.lastName || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t("account.profile.firstNameRequired")),
      lastName: Yup.string().required(t("account.profile.lastNameRequired")),
    }),
    onSubmit: handleProfileSubmit,
  });

  // Password change form validation
  const passwordValidation = useFormik<PasswordFormValues>({
    enableReinitialize: true,
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required(
        t("account.password.currentRequired")
      ),
      newPassword: Yup.string()
        .required(t("account.password.newRequired"))
        .test("num", t("account.password.invalid"), (val) => {
          const numberRegex = new RegExp(".*[0-9].*");
          if (numberRegex.test(val || "")) {
            setMissingNumber(false);
          } else {
            setMissingNumber(true);
          }
          return numberRegex.test(val || "");
        })
        .test("letter", t("account.password.invalid"), (val) => {
          const letterRegex = new RegExp("[a-zA-Z]");
          if (letterRegex.test(val || "")) {
            setMissingLetter(false);
          } else {
            setMissingLetter(true);
          }
          return letterRegex.test(val || "");
        })
        .test("len", t("account.password.invalid"), (val) => {
          const lengthRegex = new RegExp("[0-9a-zA-Z]{8,}");
          if (lengthRegex.test(val || "")) {
            setInvalidLength(false);
          } else {
            setInvalidLength(true);
          }
          return lengthRegex.test(val || "");
        }),
      confirmPassword: Yup.string()
        .required(t("account.password.confirmRequired"))
        .when("newPassword", {
          is: (val: string) => (val && val.length > 0 ? true : false),
          then: Yup.string().oneOf(
            [Yup.ref("newPassword")],
            t("account.password.mismatch")
          ),
        }),
    }),
    onSubmit: (values) => {
      dispatch(changePassword(values));
    },
  });

  const openPasswordForm = () => {
    dispatch(resetChangePasswordStatus());
    setShowPasswordForm(true);
  };

  const closePasswordForm = () => {
    passwordValidation.resetForm();
    dispatch(resetChangePasswordError());
    setShowPasswordForm(false);
  };

  // Reset and collapse the password form after a successful change
  useEffect(() => {
    if (passwordStatus === "success") {
      passwordValidation.resetForm();
      setShowPasswordForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordStatus]);

  const cancelNameEdit = () => {
    profileValidation.resetForm();
    setEditingName(false);
  };

  const cancelPictureChange = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  // Close the name editor after a successful save
  useEffect(() => {
    if (updateSuccess) setEditingName(false);
  }, [updateSuccess]);

  const saving = updateStatus === "loading" || uploadingImage;

  // One settings row: label, value and an optional action.
  const renderRow = (
    label: string,
    value: React.ReactNode,
    action?: React.ReactNode
  ) => (
    <div className="account-row">
      <div className="account-row-label">{label}</div>
      <div className="account-row-value">{value}</div>
      {action && <div className="account-row-action">{action}</div>}
    </div>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("account.pageTitle")}
          pageTitle={t("account.breadcrumbSettings")}
        />

        <div className="account-page">
          <Card>
            <CardBody>
              {updateSuccess && (
                <Alert color="success" className="mb-3">
                  {t("account.profile.updated")}
                </Alert>
              )}
              {updateError && (
                <Alert color="danger" className="mb-3">
                  {translateApiError(updateError)}
                </Alert>
              )}
              {passwordStatus === "success" && !showPasswordForm && (
                <Alert color="success" className="mb-3">
                  {t("account.password.changed")}
                </Alert>
              )}

              {/* Rows: picture, name, email, role, password */}
              <div>
                {renderRow(
                  t("account.profilePictureAlt"),
                  <>
                    <img
                      src={
                        previewUrl ||
                        userProfile?.profilePictureUrl ||
                        userDummyImg
                      }
                      alt={t("account.profilePictureAlt")}
                      className="profile-picture"
                    />
                    <input
                      type="file"
                      id="profile-picture-input"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                  </>,
                  // A new picture is saved only after confirming
                  previewUrl ? (
                    <div className="d-flex gap-2">
                      <Button
                        color="light"
                        className="btn-card-action"
                        onClick={cancelPictureChange}
                        disabled={saving}
                      >
                        {t("common.actions.cancel")}
                      </Button>
                      <Button
                        color="primary"
                        className="btn-card-action"
                        onClick={() => profileValidation.submitForm()}
                        disabled={saving}
                      >
                        {saving
                          ? t("common.saving")
                          : t("account.picture.save")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      outline
                      color="primary"
                      className="btn-card-action"
                      onClick={() =>
                        document
                          .getElementById("profile-picture-input")
                          ?.click()
                      }
                    >
                      {t("account.picture.change")}
                    </Button>
                  )
                )}

                {editingName ? (
                  <div className="account-row account-row-editing">
                    <div className="account-row-label">
                      {t("common.labels.name")}
                    </div>
                    <Form
                      onSubmit={profileValidation.handleSubmit}
                      className="account-form flex-grow-1"
                    >
                      <Row className="g-3">
                        <Col sm={6}>
                          <div className="form-group-custom">
                            <Label className="form-label-custom">
                              {t("common.labels.lastName")}
                            </Label>
                            <Input
                              type="text"
                              name="lastName"
                              className="form-input-custom"
                              value={profileValidation.values.lastName}
                              onChange={profileValidation.handleChange}
                              onBlur={profileValidation.handleBlur}
                              invalid={
                                !!(
                                  profileValidation.touched.lastName &&
                                  profileValidation.errors.lastName
                                )
                              }
                            />
                            {profileValidation.touched.lastName &&
                            profileValidation.errors.lastName ? (
                              <FormFeedback type="invalid">
                                {profileValidation.errors.lastName}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="form-group-custom">
                            <Label className="form-label-custom">
                              {t("common.labels.firstName")}
                            </Label>
                            <Input
                              type="text"
                              name="firstName"
                              className="form-input-custom"
                              value={profileValidation.values.firstName}
                              onChange={profileValidation.handleChange}
                              onBlur={profileValidation.handleBlur}
                              invalid={
                                !!(
                                  profileValidation.touched.firstName &&
                                  profileValidation.errors.firstName
                                )
                              }
                            />
                            {profileValidation.touched.firstName &&
                            profileValidation.errors.firstName ? (
                              <FormFeedback type="invalid">
                                {profileValidation.errors.firstName}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                      <div className="account-form-actions gap-2">
                        <Button
                          type="button"
                          color="light"
                          className="btn-card-action"
                          onClick={cancelNameEdit}
                        >
                          {t("common.actions.cancel")}
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          className="btn-card-action"
                          disabled={saving}
                        >
                          {saving
                            ? t("common.saving")
                            : t("common.actions.save")}
                        </Button>
                      </div>
                    </Form>
                  </div>
                ) : (
                  renderRow(
                    t("common.labels.name"),
                    formatPersonName(
                      profileValidation.values.firstName,
                      profileValidation.values.lastName
                    ),
                    <Button
                      outline
                      color="primary"
                      className="btn-card-action"
                      onClick={() => setEditingName(true)}
                    >
                      {t("common.actions.edit")}
                    </Button>
                  )
                )}

                {renderRow(t("common.labels.email"), userProfile?.email || "-")}

                {renderRow(
                  t("common.labels.role"),
                  <span className="badge bg-soft-primary text-primary fs-12">
                    {getRoleLabel(roles)}
                  </span>
                )}

                {showPasswordForm ? (
                  <div className="account-row account-row-editing">
                    <div className="account-row-label">
                      {t("account.password.sectionTitle")}
                    </div>
                    <div className="flex-grow-1">
                      {passwordError && (
                        <Alert color="danger" className="mb-3">
                          {translateApiError(passwordError)}
                        </Alert>
                      )}
                      <Form
                        onSubmit={passwordValidation.handleSubmit}
                        className="account-form"
                      >
                        <div className="form-group-custom">
                          <Label className="form-label-custom">
                            {t("account.password.currentPassword")}
                          </Label>
                          <div className="password-input-wrapper">
                            <Input
                              type={showCurrentPassword ? "text" : "password"}
                              name="currentPassword"
                              className="form-input-custom password-input"
                              value={passwordValidation.values.currentPassword}
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              invalid={
                                !!(
                                  passwordValidation.touched.currentPassword &&
                                  passwordValidation.errors.currentPassword
                                )
                              }
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                            >
                              <i
                                className={
                                  showCurrentPassword
                                    ? "ri-eye-off-line"
                                    : "ri-eye-line"
                                }
                              ></i>
                            </button>
                          </div>
                          {passwordValidation.touched.currentPassword &&
                          passwordValidation.errors.currentPassword ? (
                            <FormFeedback type="invalid" className="d-block">
                              {passwordValidation.errors.currentPassword}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="form-group-custom">
                          <Label className="form-label-custom">
                            {t("account.password.newPassword")}
                          </Label>
                          <div className="password-input-wrapper">
                            <Input
                              type={showNewPassword ? "text" : "password"}
                              name="newPassword"
                              className="form-input-custom password-input"
                              value={passwordValidation.values.newPassword}
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              invalid={
                                !!(
                                  passwordValidation.touched.newPassword &&
                                  passwordValidation.errors.newPassword
                                )
                              }
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            >
                              <i
                                className={
                                  showNewPassword
                                    ? "ri-eye-off-line"
                                    : "ri-eye-line"
                                }
                              ></i>
                            </button>
                          </div>
                          {passwordValidation.touched.newPassword &&
                          passwordValidation.errors.newPassword &&
                          !passwordValidation.values.newPassword ? (
                            <FormFeedback type="invalid" className="d-block">
                              {passwordValidation.errors.newPassword}
                            </FormFeedback>
                          ) : null}
                          {passwordValidation.touched.newPassword &&
                          passwordValidation.errors.newPassword &&
                          passwordValidation.values.newPassword ? (
                            <div className="password-requirements">
                              <p>{t("account.password.mustContain")}</p>
                              <ul>
                                <li
                                  className={
                                    missingNumber ? "invalid" : "valid"
                                  }
                                >
                                  {t("account.password.ruleNumber")}
                                </li>
                                <li
                                  className={
                                    missingLetter ? "invalid" : "valid"
                                  }
                                >
                                  {t("account.password.ruleLetter")}
                                </li>
                                <li
                                  className={
                                    invalidLength ? "invalid" : "valid"
                                  }
                                >
                                  {t("account.password.ruleLength")}
                                </li>
                              </ul>
                            </div>
                          ) : null}
                        </div>

                        <div className="form-group-custom">
                          <Label className="form-label-custom">
                            {t("account.password.confirmPassword")}
                          </Label>
                          <div className="password-input-wrapper">
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              className="form-input-custom password-input"
                              value={passwordValidation.values.confirmPassword}
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              invalid={
                                !!(
                                  passwordValidation.touched.confirmPassword &&
                                  passwordValidation.errors.confirmPassword
                                )
                              }
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              <i
                                className={
                                  showConfirmPassword
                                    ? "ri-eye-off-line"
                                    : "ri-eye-line"
                                }
                              ></i>
                            </button>
                          </div>
                          {passwordValidation.touched.confirmPassword &&
                          passwordValidation.errors.confirmPassword ? (
                            <FormFeedback type="invalid" className="d-block">
                              {passwordValidation.errors.confirmPassword}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="account-form-actions gap-2">
                          <Button
                            type="button"
                            color="light"
                            className="btn-card-action"
                            onClick={closePasswordForm}
                          >
                            {t("common.actions.cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="primary"
                            className="btn-card-action"
                            disabled={passwordStatus === "loading"}
                          >
                            {passwordStatus === "loading"
                              ? t("account.password.changing")
                              : t("account.password.submit")}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                ) : (
                  renderRow(
                    t("account.password.sectionTitle"),
                    <span className="account-password-mask">••••••••</span>,
                    <Button
                      outline
                      color="primary"
                      className="btn-card-action"
                      onClick={openPasswordForm}
                    >
                      {t("account.password.submit")}
                    </Button>
                  )
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default Account;
